import React from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Button, Checkbox, Container, Divider, FormControlLabel, Grid, Link, MenuItem, Paper, Select, Typography } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { CheckBox } from '../../../pages/motive-survey';

interface Props {
  isChecked: CheckBox;
  handleStartSurvey(): void;
  handleChangeIsChecked(name: string): (event: React.ChangeEvent<HTMLInputElement>) => void;
}

const SurveyWelcome: React.FC<Props> = ({ handleStartSurvey, isChecked, handleChangeIsChecked }) => {
  const { t, i18n } = useTranslation();
  const { classes } = useStyles();
  const motiveLogo = '/dashboard/images/motive/motiveLogo.png';
  const companyLogo = '/dashboard/images/logos/product_logo_white.svg';

  return (
    <Container maxWidth="md">
      <Paper className={classes.paper} elevation={1}>
        {/* Language selector */}
        <div className={classes.languageRow}>
          <Select
            value={i18n.language}
            onChange={(event) => i18n.changeLanguage(event.target.value)}
            size="small"
            className={classes.languageSelect}
          >
            <MenuItem value="de">Deutsch</MenuItem>
            <MenuItem value="en">English</MenuItem>
          </Select>
        </div>

        {/* Logos and greeting */}
        <Grid container alignItems="center" justifyContent="space-between">
          <Grid size={4}>
            <img src={motiveLogo} alt="Motive logo" className={classes.logo} />
          </Grid>
          <Grid size={4} className={classes.centerText}>
            <Typography variant="h4">{t('surveyStartPageGreetingTitle')}</Typography>
          </Grid>
          <Grid size={4} className={classes.rightText}>
            <img src={companyLogo} alt="Company logo" className={classes.logo} />
          </Grid>
        </Grid>

        <Typography className={classes.sectionText} variant="body1" align="center">
          {t('surveyStartPageGreeting')}
        </Typography>

        <Divider className={classes.divider} />

        {/* Instructions section */}
        <Typography variant="h4" align="center" className={classes.sectionTitle}>
          {t('surveyStartPageTitle')}
        </Typography>
        <Typography className={classes.sectionText} variant="body1" align="center">
          {t('surveyStartPageInfo')}
        </Typography>

        <Divider className={classes.divider} />

        {/* Consent section */}
        <Typography variant="h4" align="center" className={classes.sectionTitle}>
          {t('consentForm')}
        </Typography>
        <Typography className={classes.sectionText} variant="body1" align="center">
          <Trans i18nKey="surveyStartPageLegalInquiry">
            Diese Umfrage nutzt die Analysetechnologie der
            <Link className={classes.link} href="https://www.secret-ai-agent.de/" target="_blank" underline="hover">
              Worte Sprachanalyse GmbH
            </Link>
            zur Analyse Ihrer impliziten und expliziten Motive.
            <br />
            <br />
            Dabei werden ggf. Daten erhoben, verarbeitet und gespeichert, aus denen unter einem Pseudonym Profile erstellt werden. Die
            erhobenen Daten, die auch personenbezogene Daten beinhalten können, werden an SAIA übermittelt und auf deren Servern
            verarbeitet.
            <br />
            <br />
            Sie können die Erfassung Ihrer Daten durch SAIA verhindern, indem Sie es uns
            <Link className={classes.link} href="mailto:info@SAIA.de" target="_blank" underline="hover">
              hier
            </Link>
            mitteilen.
          </Trans>
        </Typography>
      </Paper>

      {/* Actions row */}
      <Grid container justifyContent="space-between" alignItems="center" className={classes.actionsRow}>
        <Grid>
          <FormControlLabel
            control={<Checkbox checked={isChecked.checked} onChange={handleChangeIsChecked('checked')} value="checked" color="primary" />}
            label={`${t('surveyAgree')}`}
            labelPlacement="start"
          />
        </Grid>
        <Grid>
          <Button variant="contained" color="primary" onClick={handleStartSurvey} disabled={!isChecked.checked} size="large">
            {t('startSurvey')}
          </Button>
        </Grid>
      </Grid>
    </Container>
  );
};

const useStyles = makeStyles()((theme) => ({
  paper: {
    marginTop: theme.spacing(3),
    padding: theme.spacing(4),
    borderRadius: theme.spacing(1.5),
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(2),
      marginTop: theme.spacing(1),
    },
  },
  languageRow: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginBottom: theme.spacing(2),
  },
  languageSelect: {
    minWidth: 120,
  },
  logo: {
    height: 60,
    width: 'auto',
    [theme.breakpoints.down('sm')]: {
      height: 40,
    },
  },
  centerText: {
    textAlign: 'center',
  },
  rightText: {
    textAlign: 'right',
  },
  sectionTitle: {
    marginTop: theme.spacing(3),
  },
  sectionText: {
    whiteSpace: 'pre-wrap',
    margin: theme.spacing(3, 0),
  },
  divider: {
    borderColor: theme.palette.secondary.main,
  },
  link: {
    textDecoration: 'none',
    color: theme.palette.secondary.main,
  },
  actionsRow: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(4),
  },
}));

export default React.memo(SurveyWelcome);
